import { useState, useEffect, useCallback } from 'react';
import { translations, Language } from '../i18n/translations';

const STORAGE_KEY = 'totemium_landing_lang';

function getInitialLanguage(): Language {
  try {
    // 1. Check localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && ['fr', 'en', 'es'].includes(stored)) {
      return stored as Language;
    }

    // 2. Check browser language
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'fr') return 'fr';
    if (browserLang === 'es') return 'es';
    
    // Default to english if not FR or ES, or fallback to FR if you prefer
    // Given user request context (FR/ES/GB), let's default to FR if no match found for local dev context, 
    // or EN if we want international default. Let's stick to FR as primary for now based on previous prompts, 
    // but usually EN is safer default. Let's use 'fr' as default per user prompt "l'application sera pour la francez..."
    return 'fr';
  } catch {
    return 'fr';
  }
}

export function useLanguage() {
  // Initialize state lazily
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      // Optional: Update HTML lang attribute for SEO/Accessibility
      document.documentElement.lang = lang;
    } catch (e) {
      // Ignore storage errors
    }
  }, []);

  // Sync HTML lang attribute on mount
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return {
    language,
    setLanguage,
    t: translations[language]
  };
}
